import { useState, useEffect } from 'react';
import { bugReportsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Popover, PopoverContent, PopoverTrigger
} from './ui/popover';
import { toast } from 'sonner';
import { Bug, Plus, Clock, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700'
};

const statusColors = {
  new: 'bg-purple-100 text-purple-700',
  in_progress: 'bg-blue-100 text-blue-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-700',
  wont_fix: 'bg-slate-100 text-slate-700'
};

/**
 * CustomerBugTracker - Bug reporting component for customer portal
 * Simplified version that only allows customers to submit bugs
 */
export const CustomerBugTracker = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [myBugs, setMyBugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    priority: 'medium',
    page_url: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchMyBugs();
    }
  }, [isOpen]);

  const fetchMyBugs = async () => {
    setLoading(true);
    try {
      const res = await bugReportsAPI.getAll();
      // Backend already filters by reported_by for portal users
      setMyBugs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch bugs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please provide a title and description');
      return;
    }
    
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        page_url: formData.page_url || window.location.pathname,
        browser_info: navigator.userAgent
      };
      await bugReportsAPI.create(data);
      toast.success('Bug report submitted! Thank you for your feedback.');
      setReportDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        priority: 'medium',
        page_url: ''
      });
      fetchMyBugs();
    } catch (error) {
      toast.error('Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative text-slate-600 hover:text-slate-900"
            data-testid="customer-bug-tracker-btn"
          >
            <Bug className="h-5 w-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <Bug className="h-4 w-4 text-red-500" />
                  Report an Issue
                </h3>
                <p className="text-xs text-muted-foreground">Help us improve your experience</p>
              </div>
              <Button size="sm" onClick={() => setReportDialogOpen(true)} data-testid="customer-report-bug-btn">
                <Plus className="h-4 w-4 mr-1" /> Report Bug
              </Button>
            </div>
          </div>
          
          <ScrollArea className="h-64">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : myBugs.length > 0 ? (
              <div className="divide-y">
                <div className="p-3 bg-slate-50">
                  <p className="text-xs font-medium text-slate-500">Your Reported Issues</p>
                </div>
                {myBugs.map(bug => (
                  <div key={bug.id} className="p-3 hover:bg-slate-50">
                    <div className="space-y-1">
                      <p className="font-medium text-sm truncate">{bug.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{bug.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`text-xs ${priorityColors[bug.priority]}`}>
                          {bug.priority}
                        </Badge>
                        <Badge className={`text-xs ${statusColors[bug.status]}`}>
                          {bug.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      {bug.resolution_notes && (
                        <p className="text-xs text-emerald-600 mt-1 border-l-2 border-emerald-400 pl-2">
                          Resolution: {bug.resolution_notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-6">
                <CheckCircle2 className="h-8 w-8 mb-2 text-emerald-500" />
                <p className="text-sm font-medium">No issues reported</p>
                <p className="text-xs text-center mt-1">
                  Found something wrong? Click &quot;Report Bug&quot; to let us know!
                </p>
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Report Bug Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-500" />
              Report an Issue
            </DialogTitle>
            <DialogDescription>
              Help us improve by reporting any problems you encounter
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label htmlFor="title">What went wrong? *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Brief summary of the issue"
                data-testid="customer-bug-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Tell us more *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what happened in detail"
                rows={3}
                data-testid="customer-bug-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="steps">How can we reproduce this?</Label>
              <Textarea
                id="steps"
                value={formData.steps_to_reproduce}
                onChange={(e) => setFormData(prev => ({ ...prev, steps_to_reproduce: e.target.value }))}
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expected">What should have happened?</Label>
                <Textarea
                  id="expected"
                  value={formData.expected_behavior}
                  onChange={(e) => setFormData(prev => ({ ...prev, expected_behavior: e.target.value }))}
                  placeholder="Expected behavior"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="actual">What actually happened?</Label>
                <Textarea
                  id="actual"
                  value={formData.actual_behavior}
                  onChange={(e) => setFormData(prev => ({ ...prev, actual_behavior: e.target.value }))}
                  placeholder="What you observed"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="priority">How serious is this?</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger data-testid="customer-bug-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Minor - It&apos;s just annoying</SelectItem>
                  <SelectItem value="medium">Moderate - It affects my work</SelectItem>
                  <SelectItem value="high">Serious - I can&apos;t do something important</SelectItem>
                  <SelectItem value="critical">Critical - Nothing works!</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReportDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="customer-submit-bug-btn">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bug className="h-4 w-4 mr-2" />}
                Submit Report
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerBugTracker;
